import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Cookie,
  Activity,
  FileText,
  Sparkles,
  Lock,
  ArrowRight,
  Code2,
  Globe,
  Zap,
  AlertTriangle,
} from "lucide-react";
import UrlScanner from "../components/UrlScanner";
import { getRecentReports, getStats } from "../lib/api";

const SAMPLE_DOMAINS = ["airbnb.com", "stripe.com", "github.com", "notion.so"];

export default function Landing() {
  const [recent, setRecent] = useState([]);
  const [stats, setStats] = useState({ total_scans: 0, total_issues_found: 0 });
  const [scannerUrl, setScannerUrl] = useState("");

  useEffect(() => {
    getRecentReports(6)
      .then((d) => setRecent(d.reports || []))
      .catch(() => {});
    getStats()
      .then(setStats)
      .catch(() => {});
  }, []);

  return (
    <main data-testid="landing-page">
      {/* HERO */}
      <section className="relative border-b border-black overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 pl-grid-bg pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 pt-16 lg:pt-24 pb-20 lg:pb-28">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-start">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#0A0A0A] text-white font-mono text-[11px] uppercase tracking-widest">
                <span className="w-1.5 h-1.5 bg-[#0047FF]" />
                AI-Powered Website Trust Intelligence
              </div>

              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-[72px] leading-[0.98] tracking-tight mt-6">
                Scan your site for{" "}
                <span className="bg-[#0047FF] text-white px-2">privacy</span>,
                <br />
                GDPR &amp; legal <em className="not-italic underline decoration-[#0047FF] decoration-[6px] underline-offset-[10px]">risks</em>
                <br />
                in <span className="font-mono text-[#0047FF]">~60s</span>.
              </h1>

              <p className="text-base md:text-lg text-[#475569] mt-6 max-w-xl leading-relaxed">
                PrivacyLens uses AI to analyze your policies, cookies, trackers,
                and user flows to surface potential compliance issues —{" "}
                <strong className="text-black">before they become expensive.</strong>
              </p>

              <div className="mt-8">
                <UrlScanner value={scannerUrl} onChange={setScannerUrl} />
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mt-3 text-xs font-mono uppercase tracking-widest text-[#475569]">
                  <span>Try:</span>
                  {SAMPLE_DOMAINS.map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => {
                        setScannerUrl(d);
                        const input = document.querySelector('[data-testid="url-scanner-input"]');
                        if (input) input.focus();
                      }}
                      className="hover:text-[#0047FF] underline underline-offset-2"
                      data-testid={`sample-domain-${d}`}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-0 mt-10 border border-[#0A0A0A]">
                <Stat label="Free Scans" value={String(stats.total_scans || 0).padStart(3, "0")} />
                <Stat label="Risks Found" value={String(stats.total_issues_found || 0).padStart(3, "0")} />
                <Stat label="Avg. Scan" value="~45s" mono />
              </div>
            </div>

            <div className="lg:col-span-5 relative">
              <div className="bg-white border border-black pl-hard-shadow">
                <div className="flex items-center justify-between px-4 py-3 border-b border-[#0A0A0A] bg-[#F8F9FA]">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-[#EF4444]" />
                    <span className="w-2.5 h-2.5 rounded-full bg-[#F59E0B]" />
                    <span className="w-2.5 h-2.5 rounded-full bg-[#10B981]" />
                  </div>
                  <div className="font-mono text-[11px] uppercase tracking-widest text-[#475569]">
                    getprivacylens.com/report/sample.com
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-mono text-[11px] uppercase tracking-widest text-[#475569]">
                        TrustScore™
                      </div>
                      <div className="font-display text-6xl mt-1 leading-none">62<span className="text-xl text-[#475569]">/100</span></div>
                      <div className="font-mono text-xs uppercase tracking-widest text-[#F59E0B] mt-1">
                        Needs Work
                      </div>
                    </div>
                    <span className="font-mono text-[11px] uppercase tracking-widest bg-[#0A0A0A] text-white px-2 py-1">
                      Scanned · just now
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-px bg-[#E2E8F0] border border-[#E2E8F0] mt-6">
                    {[
                      { l: "Privacy Policy", v: 78, c: "#10B981" },
                      { l: "Terms", v: 65, c: "#F59E0B" },
                      { l: "Cookies", v: 42, c: "#EF4444" },
                      { l: "Trackers", v: 55, c: "#F59E0B" },
                    ].map((m) => (
                      <div key={m.l} className="bg-white p-3">
                        <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569]">
                          {m.l}
                        </div>
                        <div className="flex items-end gap-1.5 mt-1">
                          <span className="font-display text-2xl leading-none">{m.v}</span>
                          <span className="text-[10px] font-mono text-[#475569] pb-0.5">/100</span>
                        </div>
                        <div className="h-1 bg-[#F1F5F9] mt-2 relative">
                          <div className="absolute inset-y-0 left-0" style={{ width: `${m.v}%`, background: m.c }} />
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6 space-y-2">
                    {[
                      { s: "high", t: "Missing CCPA-compliant 'Do Not Sell' link", c: "Privacy" },
                      { s: "medium", t: "Cookie banner missing reject-all option", c: "Cookies" },
                      { s: "low", t: "Privacy policy not dated within last 12 months", c: "Privacy" },
                    ].map((i, idx) => (
                      <div key={idx} className="flex items-start gap-3 border border-[#E2E8F0] p-3">
                        <span className={`font-mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border sev-${i.s}`}>
                          {i.s}
                        </span>
                        <div className="flex-1 text-sm">
                          <div className="font-medium">{i.t}</div>
                          <div className="text-[#475569] text-xs font-mono uppercase tracking-widest mt-0.5">
                            {i.c}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="hidden lg:block absolute -bottom-6 -left-6 -z-10 w-full h-full bg-[#0047FF]" />
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-b border-black bg-[#0A0A0A] text-white overflow-hidden">
        <div className="py-5 flex whitespace-nowrap pl-marquee">
          {Array(2).fill(0).map((_, i) => (
            <div key={i} className="flex items-center gap-12 px-6 font-mono text-sm uppercase tracking-[0.3em]">
              <span>Privacy Policy Analysis</span>
              <span className="text-[#0047FF]">◆</span>
              <span>GDPR Risk Detection</span>
              <span className="text-[#0047FF]">◆</span>
              <span>Cookie Banner Audits</span>
              <span className="text-[#0047FF]">◆</span>
              <span>Tracker Fingerprinting</span>
              <span className="text-[#0047FF]">◆</span>
              <span>Payments Compliance</span>
              <span className="text-[#0047FF]">◆</span>
              <span>Embeddable RiskBadge™</span>
              <span className="text-[#0047FF]">◆</span>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="border-b border-black" data-testid="features-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="grid md:grid-cols-12 gap-8 mb-14">
            <div className="md:col-span-5">
              <div className="font-mono text-[11px] uppercase tracking-widest text-[#0047FF] mb-3">
                / 01 — What you get
              </div>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02]">
                One scan. Every risk surface. Zero guesswork.
              </h2>
            </div>
            <div className="md:col-span-6 md:col-start-7 self-end">
              <p className="text-[#475569] text-base md:text-lg leading-relaxed">
                Most teams discover privacy bugs after a complaint, a fine, or a press
                story. PrivacyLens flips the script — read your site like a regulator would,
                in under a minute.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E2E8F0] border border-[#E2E8F0]">
            <Feature
              icon={<FileText className="w-6 h-6" />}
              title="Policy Forensics"
              body="AI reads your privacy policy + terms and flags missing GDPR clauses, vague data-retention language, and dangerous indemnity terms."
            />
            <Feature
              icon={<Cookie className="w-6 h-6" />}
              title="Cookie & Consent Audit"
              body="Detects cookie banners, reject-all flows, and consent dark patterns that auditors flag."
            />
            <Feature
              icon={<Activity className="w-6 h-6" />}
              title="Tracker Fingerprinting"
              body="Identifies GA, Pixel, Hotjar, Segment, TikTok and 20+ trackers loaded before consent."
            />
            <Feature
              icon={<ShieldCheck className="w-6 h-6" />}
              title="TrustScore™ 0–100"
              body="A single number that quantifies your privacy posture — shareable, comparable, brand-able."
            />
            <Feature
              icon={<Code2 className="w-6 h-6" />}
              title="Embeddable RiskBadge™"
              body="Drop a verified badge on your site to build customer trust. Backlink to your public report."
              accent
            />
            <Feature
              icon={<AlertTriangle className="w-6 h-6" />}
              title="Payments Risk Scan"
              body="Detects payment processors and flags missing refund, billing, and chargeback disclosures."
            />
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="border-b border-black bg-[#F8F9FA]" data-testid="how-it-works-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="font-mono text-[11px] uppercase tracking-widest text-[#0047FF] mb-3">
            / 02 — How it works
          </div>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-[1.02] max-w-3xl">
            Three steps. One honest assessment.
          </h2>

          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {[
              {
                n: "01",
                t: "Paste your URL",
                d: "Just the domain. No code, no install, no credit card.",
                icon: <Globe className="w-6 h-6" />,
              },
              {
                n: "02",
                t: "We crawl & extract",
                d: "PrivacyLens fetches your homepage, privacy, terms, and cookie pages — and detects active trackers.",
                icon: <Zap className="w-6 h-6" />,
              },
              {
                n: "03",
                t: "AI risk report",
                d: "Claude Sonnet 4.5 analyzes every signal and returns prioritized issues with concrete recommendations.",
                icon: <Sparkles className="w-6 h-6" />,
              },
            ].map((s) => (
              <div key={s.n} className="bg-white border border-black p-6 pl-hard-shadow-sm">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[11px] uppercase tracking-widest text-[#475569]">
                    Step {s.n}
                  </span>
                  <span className="w-9 h-9 grid place-items-center bg-[#0047FF] text-white">
                    {s.icon}
                  </span>
                </div>
                <h3 className="font-display text-2xl tracking-tight mt-6">{s.t}</h3>
                <p className="text-sm text-[#475569] mt-2 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RECENT */}
      <section className="border-b border-black" data-testid="recent-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="font-mono text-[11px] uppercase tracking-widest text-[#0047FF] mb-3">
                / 03 — Recently scanned
              </div>
              <h2 className="font-display text-4xl md:text-5xl tracking-tight">
                Public TrustScore reports
              </h2>
            </div>
            <Link
              to="/recent"
              className="font-mono text-[11px] uppercase tracking-widest hover:text-[#0047FF] inline-flex items-center gap-2"
              data-testid="view-all-recent-link"
            >
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {recent.length === 0 ? (
            <div className="border border-dashed border-[#E2E8F0] p-12 text-center text-[#475569] font-mono text-sm uppercase tracking-widest">
              No public reports yet — be the first to scan.
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-[#E2E8F0] border border-[#E2E8F0]">
              {recent.map((r) => (
                <Link
                  key={r.id}
                  to={`/report/${r.domain}`}
                  className="bg-white p-4 hover:bg-[#F8F9FA] transition-colors"
                  data-testid={`recent-report-${r.domain}`}
                >
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569] truncate">
                    {r.domain}
                  </div>
                  <div className="flex items-end gap-1 mt-2">
                    <span
                      className="font-display text-3xl leading-none"
                      style={{
                        color:
                          r.trust_score >= 80
                            ? "#10B981"
                            : r.trust_score >= 60
                            ? "#F59E0B"
                            : "#EF4444",
                      }}
                    >
                      {r.trust_score}
                    </span>
                    <span className="text-[10px] font-mono text-[#475569] pb-0.5">/100</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="border-b border-black bg-[#0A0A0A] text-white pl-grid-bg-dense" data-testid="cta-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28 grid md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-7">
            <div className="font-mono text-[11px] uppercase tracking-widest text-[#0047FF] mb-3">
              / 04 — Ready when you are
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02]">
              Find risks before <span className="text-[#0047FF]">regulators</span> or
              <br /> customers do.
            </h2>
          </div>
          <div className="md:col-span-5">
            <UrlScanner />
            <div className="flex items-center gap-2 mt-4 text-xs text-white/70 font-mono uppercase tracking-widest">
              <Lock className="w-3.5 h-3.5" />
              No login required for the free scan
            </div>
          </div>
        </div>
      </section>

      {/* DISCLAIMER */}
      <section className="border-b border-black" data-testid="disclaimer-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10">
          <div className="flex items-start gap-4 border border-[#E2E8F0] p-5 bg-[#F8F9FA]">
            <AlertTriangle className="w-5 h-5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
            <p className="text-sm text-[#475569] leading-relaxed">
              <strong className="text-black">PrivacyLens is not a law firm and does not provide legal advice.</strong>{" "}
              All reports, scores, recommendations, and generated documents are AI-generated assessments based on publicly available information and automated analysis.
              Results may be incomplete, inaccurate, or may not reflect your organization&apos;s full compliance posture.
              Use of PrivacyLens does not create an attorney&ndash;client relationship.
              For legal advice or compliance determinations, consult a qualified attorney.
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}

function Stat({ label, value, mono }) {
  return (
    <div className="bg-white p-5 border-r border-[#0A0A0A] last:border-r-0">
      <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569]">
        {label}
      </div>
      <div className={`font-display text-3xl leading-none mt-1 ${mono ? "font-mono" : ""}`}>
        {value}
      </div>
    </div>
  );
}

function Feature({ icon, title, body, accent }) {
  return (
    <div className={`p-6 lg:p-8 ${accent ? "bg-[#0A0A0A] text-white" : "bg-white"}`}>
      <span
        className={`inline-grid place-items-center w-10 h-10 ${
          accent ? "bg-[#0047FF] text-white" : "bg-[#0A0A0A] text-white"
        }`}
      >
        {icon}
      </span>
      <h3 className="font-display text-2xl tracking-tight mt-5">{title}</h3>
      <p className={`text-sm mt-2 leading-relaxed ${accent ? "text-white/70" : "text-[#475569]"}`}>
        {body}
      </p>
    </div>
  );
}
